import * as React from 'react';
import { cn } from '@/lib/utils';

type StatusType = 'running' | 'stopped' | 'pending' | 'error' | 'success';

interface StatusIndicatorProps extends React.HTMLAttributes<HTMLSpanElement> {
  status: StatusType;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  label?: string;
}

const statusConfig: Record<
  StatusType,
  { color: string; label: string; animate: boolean }
> = {
  running: {
    color: 'bg-success',
    label: 'Running',
    animate: true,
  },
  stopped: {
    color: 'bg-muted-foreground',
    label: 'Stopped',
    animate: false,
  },
  pending: {
    color: 'bg-warning',
    label: 'Pending',
    animate: true,
  },
  error: {
    color: 'bg-destructive',
    label: 'Error',
    animate: false,
  },
  success: {
    color: 'bg-success',
    label: 'Success',
    animate: false,
  },
};

const sizeClasses = {
  sm: 'w-1.5 h-1.5',
  md: 'w-2 h-2',
  lg: 'w-2.5 h-2.5',
};

const StatusIndicator = React.forwardRef<HTMLSpanElement, StatusIndicatorProps>(
  (
    { className, status, size = 'md', showLabel = false, label, ...props },
    ref,
  ) => {
    const config = statusConfig[status];
    const displayLabel = label || config.label;

    return (
      <span
        ref={ref}
        role="status"
        aria-label={displayLabel}
        className={cn('inline-flex items-center gap-2', className)}
        {...props}
      >
        <span
          aria-hidden="true"
          className={cn(
            'inline-block rounded-full',
            sizeClasses[size],
            config.color,
            config.animate && 'animate-pulse-dot',
          )}
        />
        {showLabel && (
          <span className="text-sm text-muted-foreground">{displayLabel}</span>
        )}
      </span>
    );
  },
);

StatusIndicator.displayName = 'StatusIndicator';

export { StatusIndicator };
export type { StatusType };
